import { useEffect, useState } from 'react'
import { ModalForm } from '@ant-design/pro-components'
import { useMutation } from '@tanstack/react-query'
import { Alert, App, Button, Descriptions, Upload } from 'antd'
import { ContactRound, FileBadge, UploadCloud } from 'lucide-react'
import { licenseValidationMessage, type LicensePreview } from '@super-mega/server/contracts/license'
import { ocrFileSchema } from '@super-mega/server/contracts/ocr'
import { useTRPC } from '@/trpc/react.ts'
import { LicenseDocumentPreview } from './license-document-preview.tsx'

type DocumentFile = { file: File; url: string }

const companyFields = [
  ['name', '企业名称'],
  ['unifiedSocialCreditCode', '信用代码'],
  ['legalRepresentative', '法定代表人'],
  ['registeredAddress', '注册地址'],
  ['registeredCapital', '注册资本'],
  ['companyType', '企业类型'],
  ['establishedOn', '成立日期'],
  ['businessScope', '经营范围']
] as const
const identityFields = [
  ['name', '姓名'],
  ['idNumber', '身份证号'],
  ['gender', '性别'],
  ['nation', '民族'],
  ['birthday', '出生日期'],
  ['address', '住址']
] as const

function previewSections(preview: LicensePreview | undefined, companyFile?: DocumentFile, identityFile?: DocumentFile) {
  return [
    {
      kind: 'company' as const,
      title: '营业执照',
      file: companyFile,
      items: preview
        ? [
            ...companyFields.map(([key, label]) => ({ key, label, children: preview.company[key] || '—' })),
            {
              key: 'validUntil',
              label: '营业期限',
              children: preview.company.isLongTerm ? '长期' : preview.company.validUntil || '—'
            }
          ]
        : []
    },
    {
      kind: 'identity' as const,
      title: '法人身份证人像面',
      file: identityFile,
      items: preview
        ? identityFields.map(([key, label]) => ({ key, label, children: preview.identity[key] || '—' }))
        : []
    }
  ]
}

function toFormData(company: File, identity: File) {
  const data = new FormData()
  data.set('company', company)
  data.set('identity', identity)
  return data
}

export function LicenseCreateModal({ onClose, onSaved }: { onClose: () => void; onSaved: () => Promise<void> }) {
  const trpc = useTRPC()
  const { message, modal } = App.useApp()
  const [companyFile, setCompanyFile] = useState<DocumentFile>()
  const [identityFile, setIdentityFile] = useState<DocumentFile>()
  const [preview, setPreview] = useState<LicensePreview>()
  const [confirmationOpen, setConfirmationOpen] = useState(false)
  const recognition = useMutation(trpc.license.preview.mutationOptions({ gcTime: 0 }))
  const save = useMutation(trpc.license.confirm.mutationOptions({ gcTime: 0 }))
  const busy = recognition.isPending || save.isPending || confirmationOpen
  const recognize = recognition.mutateAsync

  useEffect(
    () => () => {
      if (companyFile) {
        URL.revokeObjectURL(companyFile.url)
      }
    },
    [companyFile]
  )
  useEffect(
    () => () => {
      if (identityFile) {
        URL.revokeObjectURL(identityFile.url)
      }
    },
    [identityFile]
  )

  useEffect(() => {
    if (!companyFile || !identityFile) {
      return
    }
    let current = true
    void recognize(toFormData(companyFile.file, identityFile.file))
      .then((result) => {
        if (current) {
          setPreview(result)
        }
      })
      .catch(() => undefined)
    return () => {
      current = false
    }
  }, [companyFile, identityFile, recognize])

  const selectFile = (kind: 'company' | 'identity', file: File) => {
    const parsed = ocrFileSchema.safeParse(file)
    if (!parsed.success) {
      void message.error(parsed.error.issues[0]?.message ?? '图片不符合要求')
      return Upload.LIST_IGNORE
    }
    setPreview(undefined)
    recognition.reset()
    const document = { file, url: URL.createObjectURL(file) }
    if (kind === 'company') {
      setCompanyFile(document)
    } else {
      setIdentityFile(document)
    }
    return false
  }

  const confirm = () => {
    if (!preview || !companyFile || !identityFile) {
      void message.warning('请上传两份证件并等待识别完成')
      return Promise.resolve(false)
    }
    const validation = licenseValidationMessage(preview)
    if (validation) {
      void message.error(validation)
      return Promise.resolve(false)
    }
    setConfirmationOpen(true)
    modal.confirm({
      title: '确认建立执照关联？',
      content: (
        <div className="space-y-2">
          <p>企业：{preview.company.name}</p>
          <p>法定代表人：{preview.company.legalRepresentative}</p>
          <p>身份证姓名：{preview.identity.name}</p>
          <p>两份证件姓名一致。确认后保存执照、法人资料及一对一关联。</p>
        </div>
      ),
      okText: '确认保存并关联',
      cancelText: '返回核对',
      mask: { closable: false },
      afterClose: () => setConfirmationOpen(false),
      onOk: async () => {
        const data = toFormData(companyFile.file, identityFile.file)
        data.set('expected', JSON.stringify(preview))
        data.set('confirmed', 'true')
        try {
          await save.mutateAsync(data)
        } catch (error) {
          void message.error(error instanceof Error ? error.message : '关联失败，请重试')
          throw error
        }
        void message.success('执照与法人已关联')
        await onSaved()
      }
    })
    return Promise.resolve(false)
  }

  const sections = previewSections(preview, companyFile, identityFile)

  return (
    <ModalForm
      open
      title="新增执照关联"
      width="64rem"
      layout="vertical"
      onFinish={confirm}
      onOpenChange={(open) => {
        if (!open && !busy) {
          onClose()
        }
      }}
      modalProps={{ destroyOnHidden: true, closable: !busy, mask: { closable: false }, keyboard: !busy }}
      submitter={{
        searchConfig: { submitText: '确认', resetText: '取消' },
        submitButtonProps: { disabled: !preview || busy },
        resetButtonProps: { disabled: busy }
      }}
    >
      <p className="mb-4 text-sm text-slate-500 dark:text-slate-400">
        上传两份证件后自动识别，核对姓名并二次确认后保存。取消不会建档。
      </p>
      {preview ? (
        <Alert
          className="mb-4!"
          showIcon
          type={licenseValidationMessage(preview) ? 'warning' : 'success'}
          title={licenseValidationMessage(preview) ?? '两份证件姓名一致，请核对原件后点击确认。'}
          description={
            <div className="flex flex-wrap gap-x-6 gap-y-1 text-sm">
              <span>
                执照法定代表人：<strong>{preview.company.legalRepresentative || '未识别'}</strong>
              </span>
              <span>
                身份证姓名：<strong>{preview.identity.name || '未识别'}</strong>
              </span>
            </div>
          }
        />
      ) : null}
      <div className="grid grid-cols-1 items-stretch gap-4 md:grid-cols-2">
        {sections.map((section) => (
          <section key={section.kind} className="min-w-0 rounded-xl border border-slate-200 p-4 dark:border-slate-700">
            <div className="mb-3 flex items-center justify-between gap-3">
              <h3 className="m-0 flex items-center gap-2 text-sm font-semibold">
                {section.kind === 'company' ? (
                  <FileBadge aria-hidden size={18} />
                ) : (
                  <ContactRound aria-hidden size={18} />
                )}
                {section.title}
                <span className="text-red-500" aria-label="必填">
                  *
                </span>
              </h3>
              <Upload
                accept="image/jpeg,image/png,image/bmp"
                disabled={busy}
                showUploadList={false}
                beforeUpload={(file) => selectFile(section.kind, file)}
              >
                <Button size="small" disabled={busy} icon={<UploadCloud aria-hidden size={14} />}>
                  {section.file ? '更换图片' : '上传图片'}
                </Button>
              </Upload>
            </div>
            {section.file ? (
              <LicenseDocumentPreview src={section.file.url} title={section.title} />
            ) : (
              <Upload
                accept="image/jpeg,image/png,image/bmp"
                disabled={busy}
                showUploadList={false}
                beforeUpload={(file) => selectFile(section.kind, file)}
                classNames={{ root: 'block w-full', trigger: 'block! w-full' }}
              >
                <button
                  type="button"
                  disabled={busy}
                  aria-label={`上传${section.title}`}
                  className="flex h-48 w-full cursor-pointer flex-col items-center justify-center gap-2 rounded-lg border border-dashed border-slate-300 bg-slate-50 px-4 text-slate-500 transition-colors hover:border-teal-500 hover:bg-teal-50 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-teal-500 disabled:cursor-not-allowed disabled:opacity-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-400 dark:hover:border-teal-500 dark:hover:bg-slate-800"
                >
                  <UploadCloud aria-hidden size={28} strokeWidth={1.5} />
                  <span className="text-sm">点击上传{section.title}</span>
                </button>
              </Upload>
            )}
            <p className="mt-2 mb-4 truncate text-xs text-slate-400" title={section.file?.file.name}>
              {section.file ? section.file.file.name : 'JPG / PNG / BMP · 每张不超过 10 MiB'}
            </p>
            {preview ? (
              <Descriptions
                size="small"
                column={1}
                colon={false}
                classNames={{
                  label: 'w-28 min-w-28 whitespace-nowrap text-slate-500!',
                  content: 'min-w-0 break-all'
                }}
                items={section.items}
              />
            ) : (
              <p className="m-0 text-sm text-slate-400">上传后在此显示识别结果</p>
            )}
          </section>
        ))}
      </div>
      {recognition.isPending ? (
        <Alert className="mt-4!" type="info" showIcon title="正在识别两份证件，请稍候…" />
      ) : null}
      {recognition.isError ? (
        <Alert
          className="mt-4!"
          type="error"
          showIcon
          title={recognition.error.message}
          action={
            <Button
              disabled={busy}
              onClick={() => {
                if (companyFile && identityFile) {
                  void recognize(toFormData(companyFile.file, identityFile.file))
                    .then(setPreview)
                    .catch(() => undefined)
                }
              }}
            >
              重新识别
            </Button>
          }
        />
      ) : null}
    </ModalForm>
  )
}
